import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Loader2 } from "lucide-react";

function GoogleButton({ label }) {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button"
      onClick={handleGoogle}
      variant="outline"
      data-testid="google-auth-btn"
      className="w-full rounded-xl py-6 border-[#E5E5E5] hover:border-[#114A31] hover:bg-[#F9F9F8] text-[#1C1C1C]"
    >
      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
      </svg>
      {label}
    </Button>
  );
}

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("expired") === "1") {
      setError("Your session expired. Please log in again.");
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.must_change_password) navigate("/change-password");
      else if (u?.role === "tech") navigate("/tech");
      else if (u?.role === "admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="h-10 w-10 rounded-xl bg-[#114A31] flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading text-xl font-semibold text-[#1C1C1C]">ScorpPath</span>
        </Link>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl shadow-sm p-8">
          <h1 className="font-heading text-2xl font-medium text-[#1C1C1C]">Welcome back</h1>
          <p className="text-sm text-[#595D5A] mt-1">Log in to continue your conversion.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[#1C1C1C]">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                placeholder="you@business.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1C1C1C]">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                placeholder="••••••••"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-[#114A31] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            </div>
            {error && (
              <p className="text-sm text-[#EF4444]" data-testid="login-error">{error}</p>
            )}
            <Button
              type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full rounded-xl py-6 bg-[#114A31] hover:bg-[#1A6B46] text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-[#595D5A]">
            <span className="h-px flex-1 bg-[#E5E5E5]" /> or <span className="h-px flex-1 bg-[#E5E5E5]" />
          </div>
          <GoogleButton label="Continue with Google" />

          <p className="mt-6 text-center text-sm text-[#595D5A]">
            New here?{" "}
            <Link to="/register" className="text-[#114A31] font-medium hover:underline" data-testid="go-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export { GoogleButton };
